import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { X, Loader2, CheckCircle2, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
    "w-full rounded-none border border-zinc-800 bg-void px-4 py-3 text-sm text-white placeholder:text-zinc-600 outline-none transition-[border-color] duration-300 focus:border-neon";

export default function MockupModal({ open, onClose }) {
    const [form, setForm] = useState({ name: "", email: "", business_name: "", message: "", tos: false });
    const [status, setStatus] = useState("idle");

    if (!open) return null;

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const valid = form.name.trim() && /^\S+@\S+\.\S+$/.test(form.email) && form.tos;

    const submit = async () => {
        if (!valid) return;
        setStatus("sending");
        try {
            await axios.post(`${API}/quotes`, {
                name: form.name,
                email: form.email,
                business_name: form.business_name,
                service: "Free Homepage Preview",
                message: form.message,
                tos_accepted: form.tos,
            });
            setStatus("success");
        } catch (e) {
            setStatus("idle");
            toast.error("Something went wrong — please try the contact form instead.");
        }
    };

    return (
        <div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 px-4 backdrop-blur-sm"
            onClick={onClose}
            data-testid="mockup-modal"
        >
            <motion.div
                initial={{ opacity: 0, y: 30, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="relative w-full max-w-md border border-white/10 bg-surface p-8"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    aria-label="Close"
                    data-testid="mockup-close"
                    className="absolute right-4 top-4 text-zinc-500 transition-colors duration-300 hover:text-white"
                >
                    <X className="h-5 w-5" />
                </button>

                {status === "success" ? (
                    <div data-testid="mockup-success" className="py-6 text-center">
                        <CheckCircle2 className="mx-auto h-12 w-12 text-neon" />
                        <h3 className="mt-6 font-display text-2xl font-bold">You're in!</h3>
                        <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                            I'll design your free homepage preview and send it to{" "}
                            <span className="text-white">{form.email}</span>. Keep an eye on your inbox.
                        </p>
                    </div>
                ) : (
                    <>
                        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-neon">{"// Free Preview"}</p>
                        <h3 className="mt-3 font-display text-2xl font-bold leading-snug">
                            Claim your free homepage preview
                        </h3>
                        <p className="mt-2 text-sm text-zinc-500">Ten seconds. No card. No commitment.</p>
                        <div className="mt-6 space-y-4">
                            <input
                                data-testid="mockup-name-input"
                                className={inputCls}
                                placeholder="Your name *"
                                value={form.name}
                                onChange={(e) => set("name", e.target.value)}
                            />
                            <input
                                data-testid="mockup-email-input"
                                type="email"
                                className={inputCls}
                                placeholder="Email *"
                                value={form.email}
                                onChange={(e) => set("email", e.target.value)}
                            />
                            <input
                                data-testid="mockup-business-input"
                                className={inputCls}
                                placeholder="Business name"
                                value={form.business_name}
                                onChange={(e) => set("business_name", e.target.value)}
                            />
                            <input
                                data-testid="mockup-message-input"
                                className={inputCls}
                                placeholder="One line about your business"
                                value={form.message}
                                onChange={(e) => set("message", e.target.value)}
                            />
                            <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-zinc-500">
                                <input
                                    type="checkbox"
                                    data-testid="mockup-tos-checkbox"
                                    checked={form.tos}
                                    onChange={(e) => set("tos", e.target.checked)}
                                    className="mt-0.5 h-4 w-4 shrink-0 accent-[#00F0FF]"
                                />
                                <span>
                                    I agree to the{" "}
                                    <a href="/terms" target="_blank" rel="noopener noreferrer" data-testid="mockup-tos-link" className="text-neon underline underline-offset-4 hover:text-white">
                                        Terms of Service
                                    </a>
                                </span>
                            </label>
                            <button
                                onClick={submit}
                                disabled={!valid || status === "sending"}
                                data-testid="mockup-submit"
                                className="group flex w-full items-center justify-center gap-3 bg-neon px-6 py-3.5 font-mono text-xs font-medium tracking-[0.2em] uppercase text-void transition-shadow duration-300 hover:shadow-[0_0_25px_rgba(0,240,255,0.5)] disabled:opacity-50"
                            >
                                {status === "sending" ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <>
                                        Get My Free Preview
                                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                                    </>
                                )}
                            </button>
                        </div>
                    </>
                )}
            </motion.div>
        </div>
    );
}
